/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { IOnlySignupOfferModalViewProps } from './only-signup-offer-modal';
import { IImageSettings, Image, RichTextComponent } from '@msdyn365-commerce/core';
import * as Msdyn365 from '@msdyn365-commerce/core';
import Modal from 'reactstrap/lib/Modal';
import ModalBody from 'reactstrap/lib/ModalBody';
import { IHeadingData } from './only-signup-offer-modal.props.autogenerated';
var dataLayer: IGTMData[] = [];
export interface IGTMData {
    event: string;
    ecommerce: IGTMSubscribeNewLetter;
}
export interface ISignUpSessionData {
    visited?: boolean;
}
export interface IGTMSubscribeNewLetter {
    clickSubscribeNewsletter?: IGTMSubscriptionMessage;
    openNewsletterClick?: IGTMSubscriptionMessage;
    cancelNewsletterClick?: IGTMSubscriptionMessage;
}
export interface IGTMSubscriptionMessage {
    message: string;
}

const signUpForNewsletter = (): void => {
    dataLayer.push({
        event: 'Subscribe newsLetter trigger',
        ecommerce: {
            clickSubscribeNewsletter: {
                message: 'Open Sign Up form to Subscribe Newsletter'
            }
        }
    });
};

export default (props: IOnlySignupOfferModalViewProps) => {
    const renderMsdyn365Text = (heading: IHeadingData): React.ReactNode => {
        return (
            <Msdyn365.Text
                {...heading}
                tag={heading.tag || 'h2'}
                text={heading.text}
                editProps={{ onEdit: handleHeadingChange, requestContext: props.context.request }}
            />
        );
    };
    const handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => (props.config.heading!.text = event.target.value);
    const handleParagraphChange = (event: Msdyn365.ContentEditableEvent) => (props.config.paragraph = event.target.value);
    const handleTextChange = (event: Msdyn365.ContentEditableEvent) => (props.config.termsAndConditions = event.target.value);

    const newsLetterImageSettings: IImageSettings = {
        viewports: {
            xs: { q: 'w=767&h=767&m=8', w: 0, h: 0 },
            sm: { q: 'w=600&h=600&m=8', w: 0, h: 0 },
            md: { q: 'w=600&h=772&m=8', w: 0, h: 0 },
            lg: { q: 'h=772&m=8', w: 250, h: 600 }
        },
        lazyload: false,
        disableLazyLoad: true,
        cropFocalRegion: true
    };
    const imageSettingsVal: string = newsLetterImageSettings.viewports?.lg!.q!.replace(/['"]+/g, '');
    const [showModal, setShowModal] = React.useState(false);
    const signUpSession: ISignUpSessionData = props.context.request.sessionStorage.getDataFromStorage('signUpModal');

    const [agree, setAgree] = React.useState(false);

    const toggle = () => {
        props.context.request.sessionStorage.setDataInStorage('signUpModal', { visited: true });
        setShowModal(!showModal);
    };

    const checkboxHandler = () => {
        setAgree(!agree);
    };

    React.useEffect(() => {
        window.addEventListener('CookiebotOnDialogDisplay', function(e) {
            setShowModal(false);
        });

        window.addEventListener('CookiebotOnConsentReady', function(e) {
            setTimeout(function() {
                props.config.showCheckboxsignUpOfferModal && !signUpSession.visited && toggle();
            }, 10000);
        });
    }, []);

    React.useEffect(() => {
        if (showModal) {
            dataLayer.push({
                event: 'Open newsLetter trigger',
                ecommerce: {
                    openNewsletterClick: {
                        message: 'Newsletter popup has been opened for subscription'
                    }
                }
            });
        } else {
            dataLayer.push({
                event: 'Close newsLetter trigger',
                ecommerce: {
                    cancelNewsletterClick: {
                        message: 'Newsletter popup has been cancelled without subscription'
                    }
                }
            });
        }
    }, [showModal]);

    const {
        config,
        context,
        resources: { signUpButtonTitle }
    } = props;
    const { newsLetterImage, heading, paragraph, termsAndConditions } = config;
    const {
        request: {
            user: { isAuthenticated, signUpUrl }
        }
    } = context;

    return (
        <>
            <Modal isOpen={showModal && !isAuthenticated} toggle={toggle} className='Signup_popup' backdrop='static' fade={false}>
                <ModalBody style={{ padding: 0 }}>
                    <button id='cross-modal' onClick={toggle}>
                        X
                    </button>
                    <div className='row'>
                        <div className='col-md-5'>
                            {newsLetterImage && (
                                <Image
                                    src={newsLetterImage ? `${newsLetterImage?.src}` + `&` + `${imageSettingsVal}` : ''}
                                    gridSettings={props.context.request.gridSettings!}
                                    {...newsLetterImage}
                                />
                            )}
                        </div>
                        <div className='col-md-7 modal-centralized-content'>
                            <form>
                                <div className='modal-centralized-content-wrapper'>
                                    {heading && renderMsdyn365Text(heading)}
                                    <p>
                                        {paragraph && (
                                            <Msdyn365.RichTextComponent
                                                text={paragraph}
                                                editProps={{ onEdit: handleParagraphChange, requestContext: props.context.request }}
                                            />
                                        )}
                                    </p>
                                    <a
                                        style={{ color: 'white', display: 'block' }}
                                        href={signUpUrl}
                                        className={agree ? 'btn btn-primary' : 'btn btn-primary disable'}
                                        onClick={signUpForNewsletter}
                                    >
                                        {signUpButtonTitle && <RichTextComponent text={signUpButtonTitle} />}
                                    </a>
                                    <div className='custom__checkbox'>
                                        <label className='custom__checkbox__label'>
                                            <input
                                                type='checkbox'
                                                id='agree'
                                                onChange={checkboxHandler}
                                                className='custom__checkbox__input'
                                            />
                                            <p>
                                                {termsAndConditions && (
                                                    <Msdyn365.RichTextComponent
                                                        text={termsAndConditions}
                                                        className='terms-condition-links'
                                                        editProps={{ onEdit: handleTextChange, requestContext: props.context.request }}
                                                    />
                                                )}
                                            </p>
                                        </label>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </ModalBody>
            </Modal>
        </>
    );
};
